import { useState } from "react";
import { Plus, Trash2, KeyRound, Pencil, Users } from "lucide-react";
import { ApiError, errorDetail } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth-store";
import {
  Alert, Badge, Button, Field, Input, PageHeader, Select, Spinner, Switch,
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow, toast, type BadgeProps,
} from "@/components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { FormDialog } from "../components/FormDialog";
import { useCompanies } from "../companies/hooks";
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser } from "./hooks";
import type { AdminUser, CreateUserPayload, UpdateUserPayload } from "./api";

const ROLE: Record<AdminUser["role"], { label: string; variant: BadgeProps["variant"] }> = {
  super_admin: { label: "超级管理员", variant: "brand" },
  company_admin: { label: "公司管理员", variant: "info" },
  user: { label: "普通用户", variant: "neutral" },
};

function RoleBadge({ role }: { role: AdminUser["role"] }) {
  const r = ROLE[role] ?? { label: role, variant: "neutral" as const };
  return <Badge variant={r.variant} title={role}>{r.label}</Badge>;
}

const fmtLogin = (u: AdminUser) => (u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "从未");

const failMsg = (e: unknown) =>
  e instanceof ApiError ? ((e.body as { detail?: string } | null)?.detail || `失败 (${e.status})`) : "失败";

export function UsersPage() {
  const me = useAuthStore((s) => s.user);
  const isSuper = me?.role === "super_admin";

  // 默认显示全部（含停用），这样用开关停用后行不会立即消失
  const [includeInactive, setIncludeInactive] = useState(true);
  const [companyFilter, setCompanyFilter] = useState<string>("");
  const { data, isLoading, error } = useUsers(includeInactive);
  // 只有 super_admin 需要公司列表 (建用户时选公司); company_admin 请求会 403
  const companies = useCompanies(isSuper);
  const createMut = useCreateUser();
  const updateMut = useUpdateUser();
  const deleteMut = useDeleteUser();

  const [showCreate, setShowCreate] = useState(false);
  const [confirmDel, setConfirmDel] = useState<AdminUser | null>(null);
  const [pwdEdit, setPwdEdit] = useState<AdminUser | null>(null);
  const [profileEdit, setProfileEdit] = useState<AdminUser | null>(null);

  const rows = (data?.data ?? []).filter(
    (u) => !companyFilter || String(u.company_id ?? "") === companyFilter,
  );
  const colCount = isSuper ? 9 : 8;

  const companyOf = (u: AdminUser) =>
    companies.data?.data.find((c) => c.id === u.company_id)?.name ?? u.company_name ?? (u.company_id ?? "-");

  const setActive = (u: AdminUser, on: boolean) =>
    updateMut.mutate(
      { id: u.id, payload: { is_active: on ? 1 : 0 } },
      {
        onSuccess: () => toast.success(on ? `已启用 ${u.username}` : `已停用 ${u.username}`),
        onError: (e) => toast.error(on ? "启用失败" : "停用失败", { description: errorDetail(e, "请稍后重试") }),
      },
    );

  const activeSwitch = (u: AdminUser) => (
    <Switch
      checked={!!u.is_active}
      aria-label={u.is_active ? `停用 ${u.username}` : `启用 ${u.username}`}
      disabled={u.id === me?.id}
      onChange={(on) => setActive(u, on)}
    />
  );

  const actions = (u: AdminUser) => (
    <div className="inline-flex items-center gap-0.5">
      <Button variant="ghost" size="icon-sm" onClick={() => setProfileEdit(u)} aria-label="编辑" title="编辑"
        className="text-muted-foreground hover:text-primary">
        <Pencil />
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={() => setPwdEdit(u)} aria-label="改密码" title="改密码"
        className="text-muted-foreground hover:text-primary">
        <KeyRound />
      </Button>
      {u.id !== me?.id && (
        <Button variant="ghost" size="icon-sm" onClick={() => setConfirmDel(u)} aria-label="删除" title="删除"
          className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong">
          <Trash2 />
        </Button>
      )}
    </div>
  );

  const loadingBlock = (
    <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> 加载中…</span>
  );

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
      <PageHeader
        title="用户管理"
        description={isSuper ? "全部公司的账号、角色与登录状态" : "本公司成员账号与登录状态"}
        actions={
          <>
            {isSuper && (
              <Select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                aria-label="按公司筛选" className="w-36">
                <option value="">全部公司</option>
                {(companies.data?.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            )}
            <Select value={includeInactive ? "all" : "active"}
              onChange={(e) => setIncludeInactive(e.target.value === "all")}
              aria-label="按状态筛选" className="w-28">
              <option value="active">仅启用</option>
              <option value="all">全部</option>
            </Select>
            <Button icon={<Plus />} onClick={() => setShowCreate(true)}>新建用户</Button>
          </>
        }
      />

      {error && <Alert>{error instanceof ApiError ? `加载失败 (${error.status})` : "加载失败"}</Alert>}

      {/* 宽屏: 表格 */}
      <Table wrapperClassName="hidden lg:block" className="[&_td]:px-2.5 [&_th]:px-2.5">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead numeric className="w-14">ID</TableHead>
            <TableHead>用户名</TableHead>
            <TableHead className="min-w-[5rem]">显示名</TableHead>
            <TableHead>手机 / 邮箱</TableHead>
            <TableHead>角色</TableHead>
            {isSuper && <TableHead className="min-w-[5rem]">公司</TableHead>}
            <TableHead className="w-36">最后登录</TableHead>
            <TableHead className="w-16">状态</TableHead>
            <TableHead className="w-28 text-right">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <tr><td colSpan={colCount} className="py-8 text-center">{loadingBlock}</td></tr>
          )}
          {!isLoading && rows.length === 0 && <TableEmpty colSpan={colCount} icon={Users} title="暂无用户" />}
          {rows.map((u) => (
            <TableRow key={u.id} className={u.is_active ? undefined : "text-muted-foreground"}>
              <TableCell numeric className="text-muted-foreground">{u.id}</TableCell>
              <TableCell className="whitespace-nowrap font-mono text-xs">{u.username}</TableCell>
              <TableCell>
                <div className="flex min-w-0 max-w-[9rem] items-center gap-1.5">
                  <span className="truncate" title={u.display_name || undefined}>{u.display_name || "-"}</span>
                  {!u.is_active && <Badge className="shrink-0">已停用</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-xs">
                <div className="whitespace-nowrap tabular-nums">{u.phone || "-"}</div>
                {u.email && <div className="max-w-[10rem] truncate text-muted-foreground" title={u.email}>{u.email}</div>}
              </TableCell>
              <TableCell><RoleBadge role={u.role} /></TableCell>
              {isSuper && (
                <TableCell className="text-xs">
                  <div className="max-w-[7rem] truncate" title={String(companyOf(u))}>{companyOf(u)}</div>
                </TableCell>
              )}
              <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{fmtLogin(u)}</TableCell>
              <TableCell>{activeSwitch(u)}</TableCell>
              <TableCell className="text-right">{actions(u)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* 窄屏 (手机 / 平板): 卡片列表, 表格在 375px 下会把显示名挤成一字一行 */}
      <ul className="grid gap-2 sm:grid-cols-2 lg:hidden" aria-label="用户列表">
        {isLoading && <li className="py-8 text-center sm:col-span-2">{loadingBlock}</li>}
        {!isLoading && rows.length === 0 && (
          <li className="rounded-xl border bg-card py-8 text-center text-sm text-muted-foreground sm:col-span-2">暂无用户</li>
        )}
        {rows.map((u) => (
          <li key={u.id} className="rounded-xl border bg-card p-3 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="truncate font-medium text-foreground">{u.display_name || u.username}</span>
                  <RoleBadge role={u.role} />
                  {!u.is_active && <Badge>已停用</Badge>}
                </div>
                <div className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                  #{u.id} · {u.username}
                </div>
              </div>
              {activeSwitch(u)}
            </div>
            <div className="mt-2 flex items-end justify-between gap-2">
            <dl className="grid min-w-0 flex-1 grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-xs">
              <dt className="text-muted-foreground">手机</dt>
              <dd className="tabular-nums">{u.phone || "-"}</dd>
              {u.email && (<><dt className="text-muted-foreground">邮箱</dt><dd className="truncate">{u.email}</dd></>)}
              {isSuper && (<><dt className="text-muted-foreground">公司</dt><dd className="truncate">{companyOf(u)}</dd></>)}
              <dt className="text-muted-foreground">最后登录</dt>
              <dd className="tabular-nums">{fmtLogin(u)}</dd>
            </dl>
            <div className="-mb-1 -mr-1 shrink-0">{actions(u)}</div>
            </div>
          </li>
        ))}
      </ul>

      {showCreate && (
        <CreateUserForm
          isSuper={isSuper}
          companies={companies.data?.data ?? []}
          pending={createMut.isPending}
          onClose={() => setShowCreate(false)}
          onSubmit={async (p) => {
            await createMut.mutateAsync(p);
            setShowCreate(false);
            toast.success(`已创建用户 ${p.username}`);
          }}
        />
      )}

      {profileEdit && (
        <EditUserForm
          user={profileEdit}
          pending={updateMut.isPending}
          onClose={() => setProfileEdit(null)}
          onSubmit={async (payload) => {
            await updateMut.mutateAsync({ id: profileEdit.id, payload });
            setProfileEdit(null);
            toast.success("已保存");
          }}
        />
      )}

      {pwdEdit && (
        <ChangePasswordForm
          user={pwdEdit}
          pending={updateMut.isPending}
          onClose={() => setPwdEdit(null)}
          onSubmit={async (pwd) => {
            await updateMut.mutateAsync({ id: pwdEdit.id, payload: { password: pwd } });
            setPwdEdit(null);
            toast.success(`已修改 ${pwdEdit.username} 的密码`);
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title={`删除用户 "${confirmDel?.username ?? ""}"`}
        message="删除后该账号将被停用、无法登录（可通过状态开关重新启用）。"
        destructive
        confirmText="删除"
        onCancel={() => setConfirmDel(null)}
        onConfirm={() => {
          if (confirmDel) {
            const name = confirmDel.username;
            deleteMut.mutate(confirmDel.id, {
              onSuccess: () => toast.success(`已删除 ${name}`),
              onError: (e) => toast.error("删除失败", { description: errorDetail(e, "请稍后重试") }),
            });
          }
          setConfirmDel(null);
        }}
      />
    </div>
  );
}

function CreateUserForm({
  isSuper, companies, pending, onClose, onSubmit,
}: {
  isSuper: boolean;
  companies: Array<{ id: number; name: string }>;
  pending: boolean;
  onClose: () => void;
  onSubmit: (p: CreateUserPayload) => Promise<void>;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [companyId, setCompanyId] = useState<number | "">("");
  const [role, setRole] = useState<"user" | "company_admin">("user");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    if (!username.trim() || password.length < 6) {
      setErr("用户名 / 6 位密码必填");
      return;
    }
    try {
      await onSubmit({
        username: username.trim(),
        password,
        display_name: displayName.trim() || undefined,
        company_id: isSuper ? (companyId === "" ? null : Number(companyId)) : undefined,
        role: isSuper ? role : undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      });
    } catch (e) {
      setErr(failMsg(e));
    }
  };

  return (
    <FormDialog title="新建用户" pending={pending} submitText="创建" pendingText="创建中…" onClose={onClose} onSubmit={handle} err={err}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="用户名" required>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
        </Field>
        <Field label="密码" hint="至少 6 位" required>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Field>
      </div>
      <Field label="显示名" hint="可空">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="手机号" hint="可空, 绑定后可短信登录">
        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field label="邮箱" hint="可空, 填写则发送欢迎邮件">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      {isSuper && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="所属公司">
            <Select value={companyId} onChange={(e) => setCompanyId(e.target.value ? Number(e.target.value) : "")}>
              <option value="">(无 — 仅超管可创建无公司用户)</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </Field>
          <Field label="角色">
            <Select value={role} onChange={(e) => setRole(e.target.value as "user" | "company_admin")}>
              <option value="user">普通用户 (user)</option>
              <option value="company_admin">公司管理员 (company_admin)</option>
            </Select>
          </Field>
        </div>
      )}
    </FormDialog>
  );
}

function ChangePasswordForm({
  user, pending, onClose, onSubmit,
}: {
  user: AdminUser; pending: boolean; onClose: () => void; onSubmit: (pwd: string) => Promise<void>;
}) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    if (pwd.length < 6) { setErr("密码至少 6 位"); return; }
    try {
      await onSubmit(pwd);
    } catch (e) {
      setErr(failMsg(e));
    }
  };

  return (
    <FormDialog title="修改密码" description={`账号：${user.username}`} pending={pending}
      submitText="保存" pendingText="保存中…" onClose={onClose} onSubmit={handle} err={err}>
      <Field label="新密码" hint="至少 6 位">
        <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="new-password" />
      </Field>
    </FormDialog>
  );
}

function EditUserForm({
  user, pending, onClose, onSubmit,
}: {
  user: AdminUser;
  pending: boolean;
  onClose: () => void;
  onSubmit: (p: UpdateUserPayload) => Promise<void>;
}) {
  const [displayName, setDisplayName] = useState(user.display_name ?? "");
  const [phone, setPhone] = useState(user.phone ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [err, setErr] = useState<string | null>(null);

  const handle = async () => {
    setErr(null);
    // 只提交改动过的字段; 手机 / 邮箱清空 = 解绑
    const payload: UpdateUserPayload = {};
    if (displayName.trim() !== (user.display_name ?? "")) payload.display_name = displayName.trim();
    if (phone.trim() !== (user.phone ?? "")) payload.phone = phone.trim();
    if (email.trim() !== (user.email ?? "")) payload.email = email.trim();
    if (Object.keys(payload).length === 0) { onClose(); return; }
    try {
      await onSubmit(payload);
    } catch (e) {
      setErr(failMsg(e));
    }
  };

  return (
    <FormDialog title="编辑用户" description={`账号：${user.username}`} pending={pending}
      submitText="保存" pendingText="保存中…" onClose={onClose} onSubmit={handle} err={err}>
      <Field label="显示名">
        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="手机号" hint="清空 = 解绑">
        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      <Field label="邮箱" hint="清空 = 移除">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
    </FormDialog>
  );
}
